<template>
  <div class="home-workspace">
    <!-- 顶部欢迎区 -->
    <section class="workspace-header">
      <div class="header-copy">
        <h1 class="header-title">👋 欢迎回来，{{ userDisplayName }}</h1>
        <p class="header-subtitle">汽配智能管理系统 · 当前运行正常</p>
      </div>
      <div class="header-actions">
        <el-button type="primary" size="small" @click="$router.push('/orders/new')">
          <el-icon><Position /></el-icon> 快速开单
        </el-button>
        <el-button size="small" @click="$router.push('/online-orders')">
          <el-icon><ChatDotRound /></el-icon> 小程序接单
        </el-button>
        <el-button size="small" @click="$router.push('/parts')">
          <el-icon><Search /></el-icon> 查配件
        </el-button>
      </div>
    </section>

    <!-- 核心指标统计 (MpStatCard) -->
    <section class="stats-container">
      <MpStatCard 
        label="今日订单量" 
        :value="todayOrderCount" 
        class="interactive-card"
        @click="$router.push('/orders')"
      />
      <MpStatCard 
        label="挂账未结笔数" 
        :value="pendingCount" 
        :class="{ 'warn-card': pendingCount > 0 }"
        class="interactive-card"
        @click="$router.push('/customers')"
      />
      <MpStatCard 
        label="挂账待结金额" 
        :value="'¥' + pendingAmount.toLocaleString('zh-CN', { maximumFractionDigits: 0 })" 
        :class="{ 'warn-card-amount': pendingCount > 0 }"
        class="interactive-card"
        @click="$router.push('/customers')"
      />
      <MpStatCard 
        label="在库配件总数" 
        :value="partsTotal.toLocaleString('zh-CN')" 
        class="interactive-card"
        @click="$router.push('/parts')"
      />
    </section>

    <!-- 挂账提醒 -->
    <section v-if="pendingCount > 0" class="pending-alert" @click="$router.push('/customers')">
      <div class="alert-content">
        <el-icon class="alert-icon"><Warning /></el-icon>
        <span>当前有 <strong>{{ pendingCount }}</strong> 笔挂账未结，累计金额 <strong>¥{{ pendingAmount.toLocaleString('zh-CN', { maximumFractionDigits: 0 }) }}</strong>。</span>
      </div>
      <el-button type="warning" size="small" link>立即对账 →</el-button>
    </section>

    <!-- 中部网格：常用操作 (2x4) 与 最近销售单 -->
    <section class="mid-grid">
      <!-- 常用操作 (2行 x 4列) -->
      <div class="panel quick-actions-panel">
        <div class="panel-header">
          <h2 class="panel-title">常用操作</h2>
        </div>
        <div class="quick-actions-grid-2x4">
          <button class="action-tile" type="button" @click="$router.push('/orders/new')">
            <el-icon class="tile-icon"><Position /></el-icon>
            <span class="tile-title">极速开单</span>
          </button>
          <button class="action-tile" type="button" @click="$router.push('/online-orders')">
            <el-icon class="tile-icon"><ChatDotRound /></el-icon>
            <span class="tile-title">小程序订单</span>
          </button>
          <button class="action-tile" type="button" @click="$router.push('/customers')">
            <el-icon class="tile-icon"><User /></el-icon>
            <span class="tile-title">客户档案</span>
          </button>
          <button class="action-tile" type="button" @click="$router.push('/finance/daily')">
            <el-icon class="tile-icon"><DataLine /></el-icon>
            <span class="tile-title">每日对账</span>
          </button>
          <button class="action-tile" type="button" @click="$router.push('/purchase')">
            <el-icon class="tile-icon"><Download /></el-icon>
            <span class="tile-title">厂家进货列表</span>
          </button>
          <button class="action-tile" type="button" @click="$router.push('/vehicle-models')">
            <el-icon class="tile-icon"><Van /></el-icon>
            <span class="tile-title">车型工作台</span>
          </button>
          <button class="action-tile" type="button" @click="$router.push('/parts')">
            <el-icon class="tile-icon"><Box /></el-icon>
            <span class="tile-title">配件库</span>
          </button>
          <button class="action-tile" type="button" @click="$router.push('/settings')">
            <el-icon class="tile-icon"><Setting /></el-icon>
            <span class="tile-title">系统设置</span>
          </button>
        </div>
      </div>

      <!-- 最近销售单 (高密度紧凑列表) -->
      <div class="panel recent-orders-panel">
        <div class="panel-header">
          <h2 class="panel-title">最近销售单</h2>
          <el-button link type="primary" size="small" @click="$router.push('/orders')">全部销售单 →</el-button>
        </div>
        <div v-if="loadingOrders" class="loading-skeleton">
          <div v-for="i in 5" :key="i" class="skeleton-line"></div>
        </div>
        <div v-else class="orders-compact-list">
          <div v-for="order in recentOrders" :key="order.orderId" class="order-compact-row">
            <div class="order-info">
              <span class="order-cust">{{ order.customerName || '未记录客户' }}</span>
              <span class="order-id mono">{{ order.orderId }}</span>
            </div>
            <div class="order-price-status">
              <span class="order-amount">¥{{ Number(order.totalAmount || 0).toFixed(2) }}</span>
              <el-tag :type="order.payStatus === 1 ? 'success' : 'warning'" size="small" class="tag-compact">
                {{ order.payStatus === 1 ? '结清' : '挂账' }}
              </el-tag>
            </div>
          </div>
          <div v-if="recentOrders.length === 0" class="no-orders-state">
            <span>暂无近期销售单</span>
            <el-button type="primary" size="small" plain @click="$router.push('/orders/new')" style="margin-top: 8px">去开单</el-button>
          </div>
        </div>
      </div>
    </section>

    <!-- 底部网格：配件库概况 与 服务状态 -->
    <section class="bottom-grid">
      <!-- 配件分类概况 -->
      <div class="panel parts-overview-panel">
        <div class="panel-header">
          <h2 class="panel-title">配件分类概况</h2>
          <el-tag type="info" size="small" class="tag-compact">共 {{ partsTotal.toLocaleString('zh-CN') }} 款</el-tag>
        </div>
        <div class="category-grid">
          <button v-for="c in partsCategories" :key="c.name" type="button" class="category-tile" @click="$router.push('/parts')">
            <span class="cat-label">{{ c.name }}</span>
            <span class="cat-value mono">{{ c.count }}</span>
          </button>
        </div>
      </div>

      <!-- 系统服务状态 -->
      <div class="panel services-panel">
        <div class="panel-header">
          <h2 class="panel-title">服务运行状态</h2>
        </div>
        <div class="services-list">
          <div class="service-row">
            <span class="status-indicator green"></span>
            <span class="service-name">后端服务 (Go API)</span>
            <span class="service-port mono">:8888</span>
          </div>
          <div class="service-row">
            <span class="status-indicator green"></span>
            <span class="service-name">数据库 (MySQL)</span>
            <span class="service-port mono">:3306</span>
          </div>
          <div class="service-row">
            <span class="status-indicator green"></span>
            <span class="service-name">管理端后台 (Vue)</span>
            <span class="service-port mono">:8881</span>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { 
  Position, 
  ChatDotRound, 
  User, 
  DataLine, 
  Download, 
  Van, 
  Box, 
  Setting, 
  Search, 
  Warning 
} from '@element-plus/icons-vue'
import request from '@/utils/request'
import { getCurrentUser } from '@/utils/auth'
import MpStatCard from '@/components/MpStatCard.vue'

const recentOrders = ref([])
const loadingOrders = ref(true)
const partsTotal = ref(18081)
const todayOrderCount = ref(0)
const pendingCount = ref(0)
const pendingAmount = ref(0)

const currentUser = ref(getCurrentUser() || {})
const userDisplayName = computed(() => {
  return currentUser.value?.nickname || currentUser.value?.username || '管理员'
})

const partsCategories = ref([
  { name: '滤芯类', count: 2840 },
  { name: '刹车类', count: 1920 },
  { name: '传动类', count: 1560 },
  { name: '发动机', count: 2100 },
  { name: '电器类', count: 1840 },
  { name: '底盘类', count: 2200 },
  { name: '其他', count: 5621 },
])

async function loadWorkspaceStats() {
  try {
    const stats = await request.get('/api/v1/workspace/stats', { silent: true })
    todayOrderCount.value = stats?.todayOrderCount || 0
    pendingCount.value = stats?.pendingCount || 0
    pendingAmount.value = stats?.pendingAmount || 0
  } catch(e) {
    todayOrderCount.value = 0
    pendingCount.value = 0
    pendingAmount.value = 0
  }
}

async function loadRecentOrders() {
  loadingOrders.value = true
  try {
    const data = await request.get('/api/orders', { params: { page: 0, size: 5, pageSize: 5, orderType: 'lite' } })
    recentOrders.value = data?.orders || (Array.isArray(data) ? data : [])
  } catch(e) {
    recentOrders.value = []
  } finally {
    loadingOrders.value = false
  }
}

onMounted(() => {
  loadWorkspaceStats()
  loadRecentOrders()
})
</script>

<style scoped lang="scss">
.home-workspace {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* 顶部信息面板 */
.workspace-header {
  background: var(--bg-card, #111113);
  border: 1px solid var(--border-default, rgba(255,255,255,0.06));
  border-radius: 6px;
  padding: 12px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;

  .header-title {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: var(--text-primary, #e8e8e8);
  }

  .header-subtitle {
    margin: 4px 0 0;
    font-size: 11px;
    color: var(--text-secondary, #8a8a8e);
  }

  .header-actions {
    display: flex;
    gap: 8px;
  }
}

/* 核心指标容器 */
.stats-container {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
}

.interactive-card {
  cursor: pointer;
  transition: all var(--duration-fast, 100ms) var(--easing);

  &:hover {
    border-color: var(--border-focus, #3a3a3e);
    background-color: var(--bg-hover, #222226);
    transform: translateY(-1px);
  }
}

.warn-card {
  :deep(.mp-stat__value) {
    color: var(--warning, #f59e0b) !important;
  }
}

.warn-card-amount {
  :deep(.mp-stat__value) {
    color: var(--danger, #ef4444) !important;
  }
}

/* 警示条 */
.pending-alert {
  background: var(--danger-muted, rgba(239, 68, 68, 0.12));
  border: 1px solid var(--danger, #ef4444);
  border-radius: 6px;
  padding: 10px 14px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  color: var(--text-primary, #e8e8e8);
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.9;
  }

  .alert-content {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .alert-icon {
    font-size: 14px;
    color: var(--warning, #f59e0b);
  }
}

/* 中部内容排版 */
.mid-grid {
  display: grid;
  grid-template-columns: 320px 1fr;
  gap: 12px;
}

.panel {
  background: var(--bg-card, #111113);
  border: 1px solid var(--border-default, rgba(255,255,255,0.06));
  border-radius: 6px;
  padding: 14px;
  display: flex;
  flex-direction: column;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  border-bottom: 1px solid var(--border-default, rgba(255,255,255,0.04));
  padding-bottom: 8px;

  .panel-title {
    margin: 0;
    font-size: 13px;
    font-weight: 600;
    color: var(--text-primary, #e8e8e8);
    position: relative;
    padding-left: 8px;

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 2px;
      bottom: 2px;
      width: 3px;
      background: var(--accent, #3b82f6);
      border-radius: 1px;
    }
  }
}

/* 常用操作 2x4 高密度网格 */
.quick-actions-grid-2x4 {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-template-rows: repeat(2, 60px);
  gap: 8px;
}

.action-tile {
  background: var(--bg-secondary, #1a1a1e);
  border: 1px solid var(--border-default, rgba(255,255,255,0.06));
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  color: var(--text-primary, #e8e8e8);
  transition: all var(--duration-fast, 100ms) var(--easing);

  &:hover {
    background: var(--bg-hover, #222226);
    border-color: var(--border-focus, #3a3a3e);
  }

  .tile-icon {
    font-size: 16px;
    color: var(--accent, #3b82f6);
  }

  .tile-title {
    font-size: 11px;
    font-weight: 500;
  }
}

/* 最近销售单高密度列表 */
.orders-compact-list {
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: 1;
}

.order-compact-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid var(--border-default, rgba(255,255,255,0.04));

  &:last-child {
    border-bottom: none;
  }

  .order-info {
    display: flex;
    flex-direction: column;
    gap: 2px;

    .order-cust {
      font-size: 12px;
      font-weight: 500;
      color: var(--text-primary, #e8e8e8);
    }
    
    .order-id {
      font-size: 10px;
      color: var(--text-secondary, #8a8a8e);
    }
  }

  .order-price-status {
    display: flex;
    align-items: center;
    gap: 10px;

    .order-amount {
      font-size: 12px;
      font-weight: 600;
      color: var(--warning, #f59e0b);
      font-family: var(--font-mono, monospace);
    }
  }
}

.no-orders-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  font-size: 12px;
  color: var(--text-secondary, #8a8a8e);
  padding: 24px;
}

/* 底部网格 */
.bottom-grid {
  display: grid;
  grid-template-columns: 1fr 300px;
  gap: 12px;
}

.category-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 6px;
}

.category-tile {
  background: var(--bg-secondary, #1a1a1e);
  border: 1px solid var(--border-default, rgba(255,255,255,0.06));
  border-radius: 4px;
  padding: 6px 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  color: var(--text-primary, #e8e8e8);
  font-size: 11px;
  transition: all var(--duration-fast, 100ms) var(--easing);

  &:hover {
    background: var(--bg-hover, #222226);
    border-color: var(--border-focus, #3a3a3e);
  }

  .cat-label {
    color: var(--text-secondary, #8a8a8e);
  }

  .cat-value {
    font-weight: 600;
    color: var(--accent, #3b82f6);
  }
}

.services-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.service-row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: var(--text-secondary, #8a8a8e);
  padding: 4px 0;
}

.status-indicator {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  
  &.green {
    background: var(--success, #22c55e);
    box-shadow: 0 0 6px var(--success, #22c55e);
  }
}

.service-name {
  flex: 1;
}

.service-port {
  font-size: 11px;
  color: var(--text-tertiary, #5a5a5e);
}

.tag-compact {
  padding: 0 4px;
  height: 18px;
  line-height: 16px;
}

.mono {
  font-family: var(--font-mono, monospace);
}

@media (max-width: 1180px) {
  .stats-container {
    grid-template-columns: repeat(2, 1fr);
  }
  .mid-grid {
    grid-template-columns: 1fr;
  }
  .bottom-grid {
    grid-template-columns: 1fr;
  }
  .quick-actions-grid-2x4 {
    grid-template-columns: repeat(4, 1fr);
  }
}
</style>
